import React, { useState } from 'react';
import { RefreshCw, SlidersHorizontal } from 'lucide-react';
import { DnaMobileFilterSheet, useIsMobileV41 } from '@/components/dna-mobile/DnaResponsive';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { C } from './theme';

const Filter = ({ id, value, onChange, items, placeholder }) => (
  <Select value={value ? String(value) : undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={`dash-filter-${id}`}
      className="h-10 w-auto min-w-[150px] gap-2 rounded-full border-white/15 bg-white/10 text-white backdrop-blur-md focus:ring-white/30">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {items.map((i) => <SelectItem key={i.value} value={String(i.value)} data-testid={`dash-filter-${id}-${i.value}`}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Dots = () => (
  <svg className="pointer-events-none absolute right-0 top-0 h-full w-[420px] opacity-90" viewBox="0 0 420 220" aria-hidden>
    <circle cx="330" cy="70" r="78" fill={C.purple} opacity=".55" />
    <circle cx="395" cy="120" r="70" fill={C.orange} opacity=".5" />
    <circle cx="300" cy="160" r="58" fill={C.magenta} opacity=".45" />
    <circle cx="378" cy="30" r="46" fill={C.green} opacity=".55" />
    {[[220, 40, 6, C.leaf], [200, 90, 4, C.lime], [235, 150, 5, C.amber], [180, 190, 3, C.leaf]].map(([x, y, r, f], i) => <circle key={i} cx={x} cy={y} r={r} fill={f} />)}
  </svg>
);

// Versão compacta para o web administrativo em telas < 1024px: filtros em painel próprio com Aplicar/Limpar.
function DashHeaderCompact({ opts, filters, setFilters, onRefresh, loading, when }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  const units = [{ value: 'all', label: 'Todas as unidades' }, ...(opts?.units || []).map((u) => ({ value: u.id, label: u.name }))];
  const segs = [{ value: 'all', label: 'Todos os segmentos' }, ...(opts?.segments || []).map((s) => ({ value: s, label: s }))];
  const years = (opts?.years || []).map((y) => ({ value: y, label: `Ano letivo ${y}` }));
  const lbl = (arr, v) => arr.find((x) => String(x.value) === String(v))?.label;
  const summary = [lbl(years, filters.year) || 'Ano letivo', lbl(units, filters.unit_id) || 'Todas as unidades', lbl(segs, filters.segment) || 'Todos os segmentos'].join(' · ');
  const sel = (k, items) => (
    <label className="adm-m-field">
      <span>{{ year: 'Ano letivo', unit_id: 'Unidade', segment: 'Segmento' }[k]}</span>
      <select value={draft[k] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: k === 'year' ? Number(e.target.value) : e.target.value }))} data-testid={`dash-m-filter-${k}`}>
        {items.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
      </select>
    </label>
  );
  return (
    <header className="relative overflow-hidden rounded-[20px] px-4 py-4 text-white" style={{ background: C.ink }} data-testid="dash-header">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">NOVO DNA · Instituto de Aprendizagem</p>
      <h1 className="mt-1 text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dash-title">Dashboard DNA</h1>
      <p className="mt-1 text-xs text-white/70" data-testid="dash-updated-at">Última atualização: {when}</p>
      <p className="mt-2 text-sm text-white/90" data-testid="dash-m-filter-summary">{summary}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => { setDraft(filters); setOpen(true); }} data-testid="dash-m-filters-btn"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 text-sm font-bold">
          <SlidersHorizontal className="h-4 w-4" aria-hidden /> Filtros
        </button>
        <button type="button" onClick={onRefresh} disabled={loading} data-testid="dash-refresh"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white px-4 text-sm font-bold disabled:opacity-60" style={{ color: C.ink }}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden /> Atualizar
        </button>
      </div>
      <DnaMobileFilterSheet open={open} onClose={() => setOpen(false)} title="Filtros do dashboard" testId="dash-m-filter-sheet"
        footer={<div className="adm-m-sheet-actions">
          <button type="button" className="adm-m-btn ghost" data-testid="dash-m-filters-clear"
            onClick={() => setDraft({ year: opts?.years?.includes(opts?.current_year) ? opts.current_year : filters.year, unit_id: 'all', segment: 'all' })}>Limpar</button>
          <button type="button" className="adm-m-btn" data-testid="dash-m-filters-apply" onClick={() => { setFilters(draft); setOpen(false); }}>Aplicar</button>
        </div>}>
        {sel('year', years)}
        {sel('unit_id', units)}
        {sel('segment', segs)}
      </DnaMobileFilterSheet>
    </header>
  );
}

export function DashHeader({ opts, filters, setFilters, onRefresh, loading, generatedAt }) {
  const compact = useIsMobileV41(1024);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: k === 'year' ? Number(v) : v }));
  const when = generatedAt ? new Date(generatedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  if (compact) return <DashHeaderCompact opts={opts} filters={filters} setFilters={setFilters} onRefresh={onRefresh} loading={loading} when={when} />;
  return (
    <header className="relative overflow-hidden rounded-[28px] px-6 py-7 sm:px-9 sm:py-8 text-white" style={{ background: C.ink }} data-testid="dash-header">
      <Dots />
      <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/55">NOVO DNA · Instituto de Aprendizagem</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dash-title">Dashboard DNA</h1>
          <p className="mt-2 text-sm text-white/65" data-testid="dash-updated-at">Última atualização: {when}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:max-w-[720px] lg:justify-end">
          <Filter id="year" value={filters.year} onChange={set('year')} placeholder="Ano letivo"
            items={(opts?.years || []).map((y) => ({ value: y, label: `Ano letivo ${y}` }))} />
          <Filter id="unit" value={filters.unit_id} onChange={set('unit_id')} placeholder="Unidade"
            items={[{ value: 'all', label: 'Todas as unidades' }, ...(opts?.units || []).map((u) => ({ value: u.id, label: u.name }))]} />
          <Filter id="segment" value={filters.segment} onChange={set('segment')} placeholder="Segmento"
            items={[{ value: 'all', label: 'Todos os segmentos' }, ...(opts?.segments || []).map((s) => ({ value: s, label: s }))]} />
          <button type="button" onClick={onRefresh} disabled={loading} data-testid="dash-refresh"
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white px-5 text-sm font-bold transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-60"
            style={{ color: C.ink }}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
        </div>
      </div>
    </header>
  );
}
